/*
 * A commander's alliances, as their state reports them (`diplomacy`):
 * who they're allied with, who they've offered an alliance to, and who's
 * offering one to them. Allies never fire on each other, can't take each
 * other's depots, and guard each other's.
 */

import { createSignal } from "solid-js";

/** What an agreement's members grant each other. */
export interface Grants {
  /** No firing on each other, no taking each other's depots. */
  peace: boolean;
  /** Fog of war's sight shared. */
  sight: boolean;
  /** Prospecting results pooled (every 30 s). */
  findings: boolean;
  /** Last-seen memory pooled (every 30 s; the newest per cell wins). */
  sightings?: boolean;
}

/** An agreement this commander is in: any number of sides, its grants its
 *  terms. */
export interface Agreement {
  id: number;
  name: string;
  members: number[];
  grants: Grants;
  /** Sides the members promise not to fire on. */
  forbid?: number[];
  /** Members who have fired on a forbidden side since they joined. */
  breaches?: { member: number; fired_on: number }[];
}

/** A proposal: one of the commander's own or one asking them; `joins` an
 *  agreement's id when it's an invitation into one. */
export interface Proposal {
  id: number;
  by: number;
  name: string;
  members: number[];
  grants: Grants;
  /** Sides the members would promise not to fire on. */
  forbid?: number[];
  joins: number | null;
  asked: number[];
  accepted: number[];
}

export interface Diplomacy {
  allies: number[];
  /** Sides this commander has offered an alliance to (not yet accepted). */
  offered: number[];
  /** Sides offering this commander one. */
  offers: number[];
  agreements: Agreement[];
  proposals: Proposal[];
  /** Intel feeds (v31.2): your last-seen sightings given to a side once
   *  `delay` game minutes old (only your own ground's, perhaps), and those
   *  given to you. */
  feedsGiven: Feed[];
  feedsGot: Feed[];
}

export interface Feed {
  side: number;
  delay: number;
  own_ground: boolean;
  /** A bought feed ends at this tick (v31.9); null or absent: until stopped. */
  until?: number | null;
  /** A bought feed not yet paid in full is cut off at this tick (v31.10;
   *  absent: paid, or the server doesn't say). */
  unpaid_until?: number | null;
  /** May the receiver pass it on in its own feeds (v31.12; default yes)? */
  reshare?: boolean;
}

/** The state's tick (for a feed's time left). */
let stateTick = 0;
export const diplomacyTick = () => stateTick;

const [diplomacy, setDiplomacyState] = createSignal<Diplomacy>({ allies: [], offered: [], offers: [], agreements: [], proposals: [], feedsGiven: [], feedsGot: [] });
/** The commander's own side (255: watching). */
const [me, setMe] = createSignal(255);
export { diplomacy, me, setMe };

export function setDiplomacy(state: Record<string, unknown>): void {
  noteFeeds(state);
  if (typeof state.tick === "number") stateTick = state.tick;
  const d = state.diplomacy as (Partial<Diplomacy> & { feeds_given?: Feed[]; feeds_got?: Feed[] }) | undefined;
  if (!d) return;
  const next: Diplomacy = {
    allies: d.allies ?? [],
    offered: d.offered ?? [],
    offers: d.offers ?? [],
    agreements: d.agreements ?? [],
    proposals: d.proposals ?? [],
    feedsGiven: d.feeds_given ?? [],
    feedsGot: d.feeds_got ?? [],
  };
  if (JSON.stringify(next) === JSON.stringify(diplomacy())) return;
  setDiplomacyState(next);
}

export const isAlly = (side: number) => diplomacy().allies.includes(side);

/** Sides at peace with this commander through an agreement. */
export const atPeace = (side: number) => diplomacy().agreements.some((a) => a.grants.peace && a.members.includes(side) && a.members.includes(me()));

/** Sides whose sight this commander shares: allies, and members of
 *  agreements granting sight. */
export const sharingSight = (): number[] => [
  ...new Set([...diplomacy().allies, ...diplomacy().agreements.filter((a) => a.grants.sight).flatMap((a) => a.members)]),
];

/** Whether side `f` is an enemy of side `mine`: not them, not an ally, not
 *  at peace by agreement, not nobody's (255). */
export const hostile = (f: number, mine: number) => f !== mine && f !== 255 && !isAlly(f) && !atPeace(f);

/** Whether the server has intel feeds (v31.2: it lists them, even empty). */
let feedsKnown = false;
export const hasFeeds = () => feedsKnown;
export function noteFeeds(state: Record<string, unknown>): void {
  const d = state.diplomacy as Record<string, unknown> | undefined;
  feedsKnown = !!d && Array.isArray(d.feeds_given);
}
